import { Box, Flex, Text } from '@chakra-ui/react';
import { faBoxArchive } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { Banner, Button, Modal, useToast } from '@morressier/alembic';
import { CustomFormFieldDefinition } from '@publishing-engine/domain/types/article';
import { JSONSchemaType } from 'ajv';
import { FormProvider, useForm } from 'react-hook-form';
import { useRequestSubmissionWithdrawal } from 'src/api/commands';
import { CustomFormFieldComponent } from 'src/components/FormField';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { IDecisionModalProps } from '../decisionModalHelper';

export const AuthorRequestSubmissionWithdrawal = ({
  onClose,
  submissionId,
  submissionContextId
}: IDecisionModalProps) => {
  const toast = useToast();
  const {
    workflowResponse: {
      forms: { authorWithdrawSubmission },
      articleWorkflow: { id: articleWorkflowId }
    },
    refetchSubmission,
    organizationId
  } = useSubmissionData();

  const withdrawSubmissionMutation = useRequestSubmissionWithdrawal({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId,
    invalidateSubmissionQueries: refetchSubmission
  });

  const { formFields, schema } = authorWithdrawSubmission || {};

  const fields = Object.values(formFields || {}).reduce(
    (acc, val) => ({
      ...acc,
      [val.name]: val as CustomFormFieldDefinition
    }),
    {} as Record<string, CustomFormFieldDefinition>
  );

  const methods = useForm({
    resolver: ajvResolver(schema as JSONSchemaType<unknown>)
  });

  const { handleSubmit } = methods;

  const withdrawSubmission = async (formValues: unknown) => {
    const res = await withdrawSubmissionMutation.mutateAsync(formValues);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Withdrawal Request Sent',
        description: 'The editors have been notified'
      });
      onClose();
    } else {
      toast({
        variant: 'error',
        title: 'Error withdrawing submission',
        description: 'There was an error withdrawing the submission',
        isClosable: true
      });
    }
  };

  return (
    <Modal isOpen onClose={onClose} width="800px">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(withdrawSubmission)}>
          <Flex padding={8} gap={6} flexDirection="column">
            <Box>
              <FontAwesomeIcon fontSize={32} icon={faBoxArchive} />
            </Box>

            <Flex flexDirection="column" gap={2}>
              <Flex alignItems="center">
                <Text textStyle="headingGroup">Request Withdrawal?</Text>
              </Flex>
              <Text textStyle="textBody">
                Withdrawing your submission means that no further action can be
                taken and this process cannot be undone. The Editors will be
                informed of this decision via email.
              </Text>
            </Flex>

            <Flex flexDirection="column" gap={6}>
              {fields.WithdrawalReason && (
                <CustomFormFieldComponent
                  config={fields.WithdrawalReason}
                  placeholder="Select a reason for this withdrawal"
                />
              )}

              {fields.AuthorResponse && (
                <CustomFormFieldComponent
                  config={fields.AuthorResponse}
                  placeholder="Add feedback to the editor"
                />
              )}
              <Banner
                variant="attention"
                heading="For your attention"
                content="This request can be cancelled in the Decision section, but once a withdrawal is accepted, it cannot be reversed."
              />
            </Flex>

            <Flex gap={2} justifyContent="flex-end">
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={withdrawSubmissionMutation.isLoading}
              >
                Confirm Decision
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
